import { useRef, useState, type FormEvent } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { PageShell, Section } from '~/components/layout';
import {
  BusinessChoiceField,
  BusinessFormField,
  BusinessFormSuccess,
  Button,
  Grid,
} from '~/components/ui';
import {
  BUSINESS_DEMO_FIELDS,
  isChoiceField,
  OTHER_OPTION,
  otherFieldName,
  validateBusinessDemoSubmission,
  type BusinessDemoErrors,
  type BusinessDemoSubmission,
} from '~/lib/business-demo-form';
import { pageHeadMeta } from '~/lib/seo';

export const Route = createFileRoute('/business_/demo')({
  head: () =>
    pageHeadMeta({
      title: 'See nsave Payroll in Action',
      description:
        'Book a free call with the nsave payroll team. See how to pay your international team instantly, with no monthly subscription and no nsave transfer fees.',
      path: '/business/demo',
    }),
  component: BusinessDemoPage,
});

const cards = [
  {
    title: 'Visa Platinum for every employee',
    body: 'Spend worldwide from their USD salary, with purchase and price protection and car rental insurance built in.',
  },
  {
    title: 'Visa Infinite for founders',
    body: 'Our most exclusive card. It comes with 24/7 Visa Concierge, VIP perks at luxury hotels, discounted access to over 1,200 airport lounges, and travel insurance up to $1.5M.',
  },
];

function BusinessDemoPage() {
  return (
    <PageShell
      footerLegalNote={
        <p>
          <strong className="font-semibold text-ink">Disclaimer</strong>
          <br />
          The Rewards Program described in these Terms is offered and operated exclusively by nsave.
          It is a rewards program provided by nsave as an independent benefit to cardholders. This
          Rewards Program is not sponsored, administered, endorsed, or guaranteed by the
          card-issuing bank or financial institution that issues the Card (the “Card Issuer”). The
          Card Issuer bears no obligation or liability of any kind in connection with this Rewards
          Program, and any dispute or claim relating to the Rewards Program must be directed solely
          to nsave. Your Card account terms and conditions, which are governed by a separate
          agreement with the Card Issuer, continue to apply independently of these Terms.
        </p>
      }>
      <Section className="pt-16 md:pt-24">
        <Grid className="items-start gap-y-12">
          <div className="col-span-9 lg:col-span-4 lg:pr-10 lg:pt-5">
            <h1 className="max-w-2xl text-[40px] leading-[1.08] font-semibold tracking-[-0.03em] text-balance md:text-[60px]">
              See nsave Payroll in action
            </h1>
            <p className="mt-7 max-w-xl text-body leading-relaxed text-grey-900">
              Book a free call with our payroll team.
            </p>

            <div className="mt-10 max-w-xl divide-y divide-black-6 border-t border-black-6 text-body leading-relaxed text-grey-900">
              <div className="py-6">
                <p>
                  <strong className="font-semibold text-ink">
                    Premium Visa cards for your team, on us.
                  </strong>
                </p>
              </div>
              {cards.map((card) => (
                <div key={card.title} className="py-6">
                  <p className="font-semibold text-ink">💳 {card.title}</p>
                  <p className="mt-4">{card.body}</p>
                </div>
              ))}
              <p className="pt-6 text-body-s text-grey-600">
                Offer ends by end of year. Eligibility and terms apply.
              </p>
            </div>
          </div>

          <div className="col-span-9 rounded-[28px] border border-black-6 bg-grey-50 p-7 md:p-9 lg:col-span-5 lg:p-10">
            <BusinessDemoForm />
          </div>
        </Grid>
      </Section>
    </PageShell>
  );
}

const FULL_WIDTH_FIELDS = new Set(['fullName', 'companyCountry', 'nsaveAccountEmail']);

type SubmissionState =
  | { status: 'idle' | 'submitting' | 'success'; message?: never }
  | { status: 'error'; message: string };

function readForm(form: FormData): BusinessDemoSubmission {
  const values: BusinessDemoSubmission['values'] = {};
  const other: BusinessDemoSubmission['other'] = {};
  for (const field of BUSINESS_DEMO_FIELDS) {
    values[field.name] =
      field.kind === 'checkbox'
        ? form.getAll(field.name).map(String)
        : String(form.get(field.name) ?? '');
    const otherValue = form.get(otherFieldName(field.name));
    if (otherValue !== null) other[field.name] = String(otherValue);
  }
  return { values, other };
}

function BusinessDemoForm() {
  const [submission, setSubmission] = useState<SubmissionState>({ status: 'idle' });
  const [fieldErrors, setFieldErrors] = useState<BusinessDemoErrors>({});
  const [otherSelected, setOtherSelected] = useState<Record<string, boolean>>({});
  const formStartedAt = useRef(performance.now());

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = readForm(new FormData(formElement));
    const errors = validateBusinessDemoSubmission(form);
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      setSubmission({ status: 'error', message: 'Check the highlighted fields and try again.' });
      const firstInvalid = BUSINESS_DEMO_FIELDS.find((field) => errors[field.name]);
      formElement
        .querySelector<HTMLElement>(`[name="${firstInvalid?.name}"]`)
        ?.focus({ preventScroll: false });
      return;
    }

    setFieldErrors({});
    setSubmission({ status: 'submitting' });
    const payload: Record<string, unknown> = {
      ...form.values,
      fax: new FormData(formElement).get('fax'),
      elapsedMs: Math.round(performance.now() - formStartedAt.current),
    };
    for (const [name, value] of Object.entries(form.other)) payload[otherFieldName(name)] = value;

    try {
      const response = await fetch('/api/business-demo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = (await response.json().catch(() => null)) as {
        ok?: boolean;
        code?: string;
      } | null;

      if (!response.ok || !result?.ok) {
        setSubmission({
          status: 'error',
          message:
            result?.code === 'invalid_submission'
              ? 'Check the highlighted fields and try again.'
              : result?.code === 'rate_limited'
                ? 'Too many attempts. Please wait 15 minutes and try again.'
                : 'We could not send your request right now. Please try again.',
        });
        return;
      }

      formElement.reset();
      setSubmission({ status: 'success' });
    } catch {
      setSubmission({
        status: 'error',
        message: 'We could not send your request right now. Please try again.',
      });
    }
  };

  const onChange = (event: FormEvent<HTMLFormElement>) => {
    const target = event.target as HTMLInputElement;
    const field = BUSINESS_DEMO_FIELDS.find((entry) => entry.name === target.name);
    if (field && isChoiceField(field) && field.allowOther) {
      const checked = event.currentTarget.querySelector<HTMLInputElement>(
        `input[name="${field.name}"][value="${OTHER_OPTION}"]`,
      )?.checked;
      setOtherSelected((current) => ({ ...current, [field.name]: Boolean(checked) }));
    }
    const name = field?.name ?? target.name.replace(/Other$/, '');
    setFieldErrors((current) => {
      if (!current[name]) return current;
      const next = { ...current };
      delete next[name];
      return next;
    });
    if (submission.status === 'error') setSubmission({ status: 'idle' });
  };

  if (submission.status === 'success') {
    return (
      <BusinessFormSuccess title="Request received.">
        Thanks for telling us about your team. Our payroll team will get back to you within 2
        working days.
      </BusinessFormSuccess>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <h2 className="text-[24px] leading-[1.2] font-semibold tracking-[-0.025em] text-ink md:text-[26px]">
          Request a free payroll call
        </h2>
        <p className="max-w-xl text-body leading-[1.5] text-grey-700">
          Tell us a bit about your business and we’ll get back to you within 2 working days.
        </p>
      </div>
      <form
        className="mt-8 grid gap-x-5 gap-y-6 md:grid-cols-2"
        onSubmit={onSubmit}
        onChange={onChange}
        noValidate>
        {BUSINESS_DEMO_FIELDS.map((field) =>
          isChoiceField(field) ? (
            <BusinessChoiceField
              key={field.name}
              id={`business-demo-${field.name}`}
              name={field.name}
              label={field.label}
              type={field.kind}
              options={field.options}
              otherValue={field.allowOther ? OTHER_OPTION : undefined}
              otherName={otherFieldName(field.name)}
              otherSelected={otherSelected[field.name]}
              error={fieldErrors[field.name]}
              className="md:col-span-2"
            />
          ) : (
            <BusinessFormField
              key={field.name}
              id={`business-demo-${field.name}`}
              name={field.name}
              type={field.kind}
              label={field.label}
              required={field.required}
              autoComplete={field.autoComplete}
              placeholder={field.placeholder}
              maxLength={field.maxLength}
              error={fieldErrors[field.name]}
              className={FULL_WIDTH_FIELDS.has(field.name) ? 'md:col-span-2' : undefined}
            />
          ),
        )}
        <input
          type="text"
          name="fax"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="absolute -left-[10000px] h-px w-px overflow-hidden"
        />
        {submission.status === 'error' && (
          <div
            role="alert"
            className="rounded-[16px] border border-red-500/20 bg-red-500/5 px-4 py-3 text-body-s text-red-500 md:col-span-2">
            {submission.message}
          </div>
        )}
        <Button
          type="submit"
          className="mt-1 min-h-14 w-full md:col-span-2"
          disabled={submission.status === 'submitting'}>
          {submission.status === 'submitting' ? (
            <span className="flex items-center justify-center gap-2">
              <span className="size-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
              Sending…
            </span>
          ) : (
            'Request a free call'
          )}
        </Button>
        <p className="text-[13px] leading-[1.55] text-grey-600 md:col-span-2">
          We’ll only use these details to follow up on your request. See our{' '}
          <Link to="/privacy-policy" className="font-medium text-ink underline underline-offset-4">
            privacy policy
          </Link>
          .
        </p>
      </form>
    </>
  );
}
